import type { MutableRefObject } from "react";

import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { ReRenderCanvas } from "@/Utils/StateLib";
import type { StatusController } from "@/hooks/useStatusController";
import { appstr } from "@coreapp/utils/appstr";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { zoomToFit } from "./PreviewCanvas.utils";

type Props = {
  statusController: StatusController;
  mockDocumentRef: MutableRefObject<QXDoc | null>;
};

export const PreviewPageControls = (props: Props) => {
  const scm = props.mockDocumentRef.current?.GetSubChartMgr();

  const currentSc = scm?.GetCurrentSubChart();
  const currentPage = currentSc?.GetPageNumber() ?? 1;
  const lastPage = scm?.GetActiveBranchPages();

  const handleNav = (targetPage: number) => () => {
    const targetSc = scm?.GetSubChartByPageNum(targetPage);
    const mockDoc = props.mockDocumentRef.current;
    const mockCanvasPanel = mockDoc?.GetCanvasRef();
    mockCanvasPanel?.SwitchToSubChartByObj(targetSc);
    if (mockDoc) zoomToFit(mockDoc);
    ReRenderCanvas();
  };

  const isFormDisabled = !props.statusController.isReady;

  return (
    <Flex
      padding={"12px 0"}
      direction={"row"}
      gap={"8px"}
      width={"100%"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <Text size={"small"}>
        {appstr("Page")} {currentPage} {appstr("of")} {lastPage}
      </Text>
      <Flex direction={"row"} gap={"6px"}>
        <Button
          dataTestId="preview-page-controls-first-page"
          modifier={"secondary"}
          size={"small"}
          iconProps={{
            name: "backward-step_",
            size: "small"
          }}
          disabled={isFormDisabled || currentPage === 1}
          onClick={handleNav(1)}
        />
        <Button
          dataTestId="preview-page-controls-prev-page"
          disabled={isFormDisabled || currentPage === 1}
          onClick={handleNav(currentPage - 1)}
          modifier={"secondary"}
          size={"small"}
          iconProps={{ name: "caret-left", size: "small" }}
        >
          {appstr("Prev")}
        </Button>
        <Button
          dataTestId="preview-page-controls-next-page"
          disabled={isFormDisabled || currentPage === lastPage}
          onClick={handleNav(currentPage + 1)}
          modifier={"secondary"}
          size={"small"}
          iconAlignEnd
          iconProps={{ name: "caret-right", size: "small" }}
        >
          {appstr("Next")}
        </Button>
        <Button
          dataTestId="preview-page-controls-last-page"
          modifier={"secondary"}
          size={"small"}
          iconProps={{
            name: "forward-step_",
            size: "small"
          }}
          disabled={isFormDisabled || currentPage === lastPage}
          onClick={handleNav(lastPage)}
        />
      </Flex>
    </Flex>
  );
};
